<template>
  <TransitionRoot as="template" :show="modalOpen">
    <Dialog as="div" class="relative z-[100]">
      <TransitionChild
        as="template"
        enter="ease-out duration-300"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-gray-900/50 backdrop-blur-sm transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-[100] overflow-y-auto">
        <div class="modal-scroll-root">
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="modal-panel relative flex w-full min-w-0 transform flex-col gap-4 overflow-hidden rounded-2xl bg-white p-5 text-left shadow-xl ring-1 ring-black/5 transition-all sm:p-6"
              :class="wide ? 'max-w-2xl' : 'max-w-lg'"
            >
              <div
                v-if="showSuccessIcon || showFailureIcon"
                class="mx-auto flex h-12 w-12 shrink-0 items-center justify-center rounded-full"
                :class="{
                  'bg-green-100': showSuccessIcon,
                  'bg-red-100': showFailureIcon,
                }"
              >
                <CheckIcon
                  v-if="showSuccessIcon"
                  class="h-6 w-6 text-green-600"
                  aria-hidden="true"
                />
                <XMarkIcon v-else class="h-6 w-6 text-red-600" aria-hidden="true" />
              </div>

              <div
                v-if="headerText"
                class="min-w-0 w-full shrink-0"
                :class="wide || showCloseButton ? 'text-left' : 'text-center'"
              >
                <div v-if="showCloseButton" class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1">
                    <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                      {{ headerText }}
                    </DialogTitle>
                    <template v-if="transactionIdValue !== null">
                      <p class="mt-1 text-sm text-gray-500">Transaction Id:</p>
                      <p
                        class="subheader-text subheader-text--mono mt-1 min-w-0 font-mono text-xs leading-relaxed text-gray-600"
                      >
                        {{ transactionIdValue }}
                      </p>
                    </template>
                    <p
                      v-else-if="subheaderText"
                      class="subheader-text mt-1 min-w-0 text-sm leading-relaxed text-gray-500"
                    >
                      {{ subheaderText }}
                    </p>
                  </div>
                  <button
                    type="button"
                    class="-m-1 shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                    @click="cancelAction"
                  >
                    <span class="sr-only">Close</span>
                    <XMarkIcon class="h-5 w-5" aria-hidden="true" />
                  </button>
                </div>
                <template v-else>
                  <DialogTitle as="h3" class="text-base font-semibold leading-6 text-gray-900">
                    {{ headerText }}
                  </DialogTitle>
                  <template v-if="transactionIdValue !== null">
                    <p class="mt-1 text-sm text-gray-500">Transaction Id:</p>
                    <p
                      class="subheader-text subheader-text--mono mt-1 min-w-0 font-mono text-xs leading-relaxed text-gray-600"
                    >
                      {{ transactionIdValue }}
                    </p>
                  </template>
                  <p
                    v-else-if="subheaderText"
                    class="subheader-text mt-1 min-w-0 text-sm leading-relaxed text-gray-500"
                  >
                    {{ subheaderText }}
                  </p>
                </template>
              </div>

              <div v-if="$slots.default || modalImgUrl" class="modal-body min-w-0">
                <div v-if="$slots.default" class="min-w-0">
                  <slot></slot>
                </div>
                <div v-if="modalImgUrl" class="text-center">
                  <img
                    :src="modalImgUrl"
                    alt="QR code to share donation"
                    class="inline-block rounded-2xl border border-gray-200 bg-white p-3 shadow-sm"
                  />
                </div>
              </div>

              <div
                v-if="ctaText && (!hideCtaOnSuccess || !successState)"
                class="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end"
              >
                <BaseButton
                  v-if="showCancelButton && !successState"
                  type="button"
                  variant="secondary"
                  block
                  class="sm:w-auto"
                  @click="cancelAction"
                >
                  Cancel
                </BaseButton>
                <BaseButton
                  type="button"
                  variant="primary"
                  block
                  class="sm:w-auto"
                  :disabled="disableCTA"
                  @click="closeModal"
                >
                  {{ ctaText }}
                </BaseButton>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'
import { CheckIcon, XMarkIcon } from '@heroicons/vue/24/outline'

export default {
  name: 'Modal',
  components: {
    Dialog,
    DialogPanel,
    DialogTitle,
    TransitionChild,
    TransitionRoot,
    CheckIcon,
    XMarkIcon,
  },
  props: {
    headerText: {
      type: String,
      default: '',
    },
    ctaText: {
      type: String,
      default: '',
    },
    subheaderText: {
      type: String,
      default: '',
    },
    modalOpen: {
      type: Boolean,
    },
    modalImgUrl: {
      type: String,
      default: '',
    },
    showSuccessIcon: {
      type: Boolean,
    },
    showFailureIcon: {
      type: Boolean,
    },
    showCancelButton: {
      type: Boolean,
      default: false,
    },
    successState: {
      type: Boolean,
      default: false,
    },
    hideCtaOnSuccess: {
      type: Boolean,
      default: false,
    },
    wide: {
      type: Boolean,
      default: false,
    },
    disableCTA: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    showCloseButton() {
      return (
        this.showCancelButton &&
        !this.successState &&
        !this.showSuccessIcon &&
        !this.showFailureIcon
      )
    },
    transactionIdValue() {
      const prefix = 'Transaction Id:\n'
      if (this.subheaderText?.startsWith(prefix)) {
        return this.subheaderText.slice(prefix.length)
      }
      const inlinePrefix = 'Transaction Id: '
      if (this.subheaderText?.startsWith(inlinePrefix)) {
        return this.subheaderText.slice(inlinePrefix.length)
      }
      return null
    },
  },
  methods: {
    closeModal() {
      this.$emit('closeModal')
    },
    cancelAction() {
      this.$emit('cancelAction')
    },
  },
}
</script>

<style scoped>
.modal-scroll-root {
  display: flex;
  min-height: 100%;
  align-items: flex-end;
  justify-content: center;
  padding: max(1rem, env(safe-area-inset-top, 0px)) max(1rem, env(safe-area-inset-right, 0px))
    max(1rem, env(safe-area-inset-bottom, 0px)) max(1rem, env(safe-area-inset-left, 0px));
  text-align: center;
}

@media (min-width: 640px) {
  .modal-scroll-root {
    align-items: center;
    padding: max(1.5rem, env(safe-area-inset-top, 0px)) max(1.5rem, env(safe-area-inset-right, 0px))
      max(1.5rem, env(safe-area-inset-bottom, 0px)) max(1.5rem, env(safe-area-inset-left, 0px));
  }
}

.modal-panel {
  max-height: calc(100vh - 2rem);
  max-height: calc(
    100dvh - 2rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
  );
}

@media (min-width: 640px) {
  .modal-panel {
    max-height: calc(100vh - 3rem);
    max-height: calc(
      100dvh - 3rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)
    );
  }
}

.modal-body {
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
}

/* Ordinary copy wraps on word boundaries. Long IDs may still break mid-token. */
.subheader-text {
  overflow-wrap: break-word;
  word-break: normal;
  hyphens: none;
}

.subheader-text--mono {
  overflow-wrap: anywhere;
}
</style>
